/** @format */
import {
	Chain,
	PromotionDetailsMapV1,
	PromotionDetailsMapV2,
	PromotionDetailsV1,
	PromotionDetailsV2
} from '@shared/data-access/interfaces';
import {
	Component,
	ComponentRef,
	computed,
	DestroyRef,
	inject,
	input,
	OnInit,
	TemplateRef,
	viewChild
} from '@angular/core';
import {
	HomefieldIconLibraryModule,
	homefieldIconOffer,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { BypassSanitizationPipe, ObjectKeysPipe } from '@shared/utils/pipes';

import { CommonModule } from '@angular/common';
import { DiscountEligibilityService } from '@shared/data-access/product';
import { Store } from '@ngrx/store';
import { selectResolvedSku } from '@pdp/data-access/state';
import { take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'pdp-promotion',
	templateUrl: './promotion.component.html',
	styleUrl: './promotion.component.scss',
	imports: [CommonModule, HomefieldIconLibraryModule, BypassSanitizationPipe, ObjectKeysPipe]
})
export class PromotionComponent implements OnInit {
	private readonly chain = inject(Chain);
	private readonly store = inject(Store);
	private readonly discountEligibilityService = inject(DiscountEligibilityService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	readonly promotions = input<PromotionDetailsMapV1 | PromotionDetailsMapV2>();

	readonly addFlexBox = input(false);

	readonly textContainerClasses = input('hmf-mb-xs hmf-mb-m-s');

	// If set, the promotion text opens a URL instead of a side panel
	readonly linkHref = input<string>();

	readonly isMobile = input(false);

	public chainIdentifierAbbr = this.chain.chainIdentifierAbbr;

	public heading = 'Promotion Details';

	public ecodePromotion: PromotionDetailsV1 | PromotionDetailsV2;

	public currentPromotions: PromotionDetailsMapV1 | PromotionDetailsMapV2;

	readonly textContainerClassList = computed(
		() => `promotion-description-text-container ${this.textContainerClasses()}`
	);

	private readonly modalFactoryService = inject(HmfModalFactoryService);

	private drawerRef: ComponentRef<HmfDrawerComponent>;

	readonly promotionDrawerTemplateRef =
		viewChild.required<TemplateRef<unknown>>('promotionDrawerContent');
	readonly ecodePromotionDrawerTemplateRef = viewChild.required<TemplateRef<unknown>>(
		'ecodePromotionDrawerContent'
	);
	private readonly destroyRef = inject(DestroyRef);

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconOffer]);
	}

	ngOnInit() {
		this.ecodePromotion = this.promotions()?.['ecode'] || undefined;
		this.currentPromotions = this.promotions();

		this.store
			.select(selectResolvedSku)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resolvedSku) => {
				this.getSkuPromotionData(resolvedSku?.parentPartNumber, resolvedSku?.partNumber);
			});
	}

	getSkuPromotionData(ecode: string, skuId: string) {
		if (!skuId) {
			this.currentPromotions = this.promotions();
			return;
		}

		const promoMessageSubscription = this.discountEligibilityService.getPromoMessage$(
			ecode,
			skuId
		);

		if (!promoMessageSubscription) {
			this.currentPromotions = this.promotions();
			return;
		}

		promoMessageSubscription
			.pipe(take(1), takeUntilDestroyed(this.destroyRef))
			.subscribe((promoMessage) => {
				const message = promoMessage?.itemPromoMessage
					? promoMessage.itemPromoMessage
					: promoMessage?.shippingPromoMessage;
				if (message) {
					this.currentPromotions = {
						ProductLevelPromotion: {
							longDesc: '',
							pdpDesc: message.pdpItemMessage
								? message.pdpItemMessage
								: message.pdpShippingMessage,
							promoDesc: message.pdpItemMessage
								? message.pdpItemMessage
								: message.pdpShippingMessage,
							promotionId: message.discountId,
							adminName: '',
							catentryId: '',
							groupName: '',
							priority: 0,
							promoFreeGift: ''
						}
					};
				} else {
					this.currentPromotions = this.promotions();
				}
			});
	}

	onPromoClick() {
		this.fetchPromoDescription();
		this.openDrawer();
	}

	openDrawer(ecodePromotion = false) {
		if (!this.drawerRef) {
			this.drawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					headerText: this.heading,
					closable: true,
					direction: this.isMobile() ? 'bottom' : 'right',
					isCloseButtonSticky: true,
					size: this.isMobile() ? 'large' : 'medium'
				},
				ecodePromotion
					? this.ecodePromotionDrawerTemplateRef()
					: this.promotionDrawerTemplateRef()
			);
		}

		this.drawerRef.instance?.openDrawer();
	}

	fetchPromoDescription() {
		if (
			!this.isPromotionDetailsMapV1(this.currentPromotions) ||
			this.currentPromotions.ProductLevelPromotion.longDesc
		) {
			return;
		}

		if (this.currentPromotions.ProductLevelPromotion?.promotionId) {
			this.discountEligibilityService
				.getPromoDescription$(
					undefined,
					undefined,
					this.currentPromotions.ProductLevelPromotion?.promotionId
				)
				.pipe(take(1), takeUntilDestroyed(this.destroyRef))
				.subscribe((promoDescription) => {
					this.currentPromotions = {
						ProductLevelPromotion: {
							...this.currentPromotions.ProductLevelPromotion,
							longDesc: promoDescription
						}
					} as PromotionDetailsMapV1;
				});
		} else {
			this.currentPromotions = {
				ProductLevelPromotion: {
					...this.currentPromotions.ProductLevelPromotion,
					longDesc:
						"<p class='hmf-m-xs hmf-subheader-s'>Select options for more details<p>"
				}
			};
		}
	}

	private isPromotionDetailsMapV1(promoDetails: unknown): promoDetails is PromotionDetailsMapV1 {
		return Boolean(
			(promoDetails as PromotionDetailsMapV1)?.ProductLevelPromotion !== undefined
		);
	}

	trackByFn(index: number, item: PromotionDetailsMapV1): string {
		return index.toString() + item?.ProductLevelPromotion?.promotionId;
	}
}
